import cn from 'classnames';

import { pluralize } from 'helpers';
import { buildRecosKey } from 'helpers/RecoUtils';

export function makeHeartClickHandler(heartEventData, clickHandlerDataAndActions) {
  const { heartEventName, unHeartEventName } = heartEventData;
  const { heartProduct, unHeartProduct, toggleHeartingLoginModal, trackEvent, isCustomer } = clickHandlerDataAndActions;
  return function({ styleId }, isHearted) {
    if (isCustomer) {
      if (isHearted) {
        unHeartProduct({ itemId: styleId });
        trackEvent(unHeartEventName, styleId);
      } else {
        heartProduct({ itemId: styleId });
        trackEvent(heartEventName, styleId);
      }
    } else {
      toggleHeartingLoginModal(true, styleId);
    }
  };
}

export function isProductHearted(showFavoriteHeart, hearts = [], styleId) {
  return showFavoriteHeart && styleId && hearts?.indexOf(styleId) > -1;
}

export function makeFavoriteHeartButton({
  cssHeartActive,
  cssHeartContainer,
  extraRecoStyle = null,
  forceShowHearted = null,
  handleHeartClick,
  isUninteractive = false,
  hearts,
  heartsList,
  showFavoriteHeart,
  style,
  productId,
  compactCount = false,
  isDisplayCount = true,
  testId = null
}) {
  const { styleId } = style;
  if (heartsList && showFavoriteHeart) {
    const isHearted = typeof forceShowHearted === 'boolean' ? forceShowHearted : isProductHearted(showFavoriteHeart, hearts, styleId);
    let count = (heartsList[styleId] > 0 || !isHearted) ? heartsList[styleId] : 1;

    // this delays the new product card from rendering the icon until count is present.
    if (typeof count !== 'number' && compactCount) {
      return null;
    }

    if (compactCount) {
      count = count > 1000 ? `${Math.floor(count / 1000)}k` : count;
    }

    if (isUninteractive) {
      return (
        <span
          data-test-id={testId}
          className={cn(cssHeartContainer, { [cssHeartActive]: isHearted }, extraRecoStyle)}
          aria-label={`This item has been favorited ${count} ${pluralize('time', count)}.`}>
          {count}
        </span>
      );
    }
    return (
      <button
        type="button"
        className={cn(cssHeartContainer, { [cssHeartActive]: isHearted }, extraRecoStyle)}
        aria-label={`${isHearted ? 'Remove' : 'Add'} this item ${isHearted ? 'from' : 'to'} favorites. It has been favorited ${count} ${pluralize('time', count)}.`}
        onClick={handleHeartClick(style, productId)}
        data-test-id={testId}>
        {isDisplayCount && count}
      </button>
    );
  }
  return null;
}

export function makeHandleHeartButtonClick({
  hearts,
  onHeartClick,
  onCollectionToggle,
  productId,
  showFavoriteHeart,
  style
}) {
  return e => {
    const { styleId, basePrice, salePrice } = style;
    const price = basePrice !== salePrice ? salePrice : basePrice;
    const isHearted = isProductHearted(showFavoriteHeart, hearts, styleId);

    // Prevent incorrect tracking events and going to product url
    e.stopPropagation();
    e.preventDefault();
    // Run hearting callback if present
    if (onHeartClick) {
      onCollectionToggle && onCollectionToggle(!isHearted); // show collection if product has not been hearted prior to clicking "Heart"
      onHeartClick({
        styleId,
        productId,
        price
      }, isHearted);
    }
  };
}

// combines list object information with the items
export function makeCollectionInformationWithItems(lists, collections) {
  const collectionWithItems = [];
  if (lists?.length && collections?.length) {
    lists.forEach(list => {
      const correspondingCollection = collections.find(collection => list.listId === collection.listId);
      collectionWithItems.push({
        ...list, ...correspondingCollection
      });
    });
  }

  return collectionWithItems;
}

export function getHeartProps(heartProps, { heartEventName, unHeartEventName }) {
  const {
    hasHearting,
    isCustomer,
    heartProduct,
    hearts,
    products = {},
    toggleHeartingLoginModal,
    trackEvent,
    unHeartProduct,
    isDisplayCount
  } = heartProps || {};
  if (hasHearting) {
    const clickHandlerDataAndActions = { heartProduct, isCustomer, toggleHeartingLoginModal, trackEvent, unHeartProduct };
    return {
      showFavoriteHeart: true,
      onHeartClick: makeHeartClickHandler({ heartEventName, unHeartEventName }, clickHandlerDataAndActions),
      hearts,
      heartsList: products.heartsList,
      isDisplayCount
    };
  }

  return { showFavoriteHeart: false };
}

export function getHeartingStyleIdsForComponent(slotData = {}, recommenderEntries = {}) {
  const results = [];
  Object.values(slotData).forEach(slot => {
    switch (slot.componentName) {
      case 'genericBrandTrending':
        results.push(...(slot.trending?.results || []));
        break;
      case 'productSearch':
        results.push(...(slot.products || []));
        break;
      case 'melodyGrid':
        results.push(...(slot.products || []));
        break;
      case 'recommender':
        const { janus = {} } = recommenderEntries;
        const recosKey = buildRecosKey(slot);
        const { recos } = janus[recosKey] || {};
        if (recos?.length > 0) {
          results.push(...recos);
        }
        break;
    }
  });
  return results
    .map(result => ({ styleId: result.styleId }))
    .filter(result => !!result);
}
